/**
 * Outline shapes: an outline clicked onto the splat surface, fitted to a plane
 * (utils/surfacePolygon) and extruded `depth` in front of and behind it into a slab. Shared by
 * annotation layer shapes and focus / haze regions drawn as outlines, so an outline covers the
 * same splats whichever feature it belongs to.
 *
 * Points are runtime scene coordinates: the space `raycastScreenPoint` returns and measurements
 * use, NOT the Z-mirrored editor coordinates of sphere / box regions (see area-shape.ts).
 *
 * The slab is baked into the shader as straight-line code with literal numbers (see the layout
 * note above outlineSlabShader): editing an outline recompiles once, while nothing per frame does.
 */
import { shaderNumber } from './region-shape';
import { cleanSurfacePoints, fitSurfaceOutline, type SurfaceOutlineFit, type SurfacePoint, type SurfacePolygon } from '../utils/surfacePolygon';

/** Points one outline may have (the editor's outline tool stops here). */
export const MAX_OUTLINE_POINTS = 48;
/** Scene coordinates beyond this are treated as corrupt (they would also print as invalid shader literals). */
export const MAX_OUTLINE_COORDINATE = 1e6;

/**
 * An outline ready to test or print: its fitted plane, thickness and soft edge (scene units). The
 * plane ignores clicks that missed the surface (fitSurfaceOutline), so one click on a tree over a
 * lawn doesn't tilt the slab or make it metres thick.
 */
export interface OutlineSlab {
    fit: SurfaceOutlineFit;
    /** How far in front of and behind the surface the slab reaches. */
    depth: number;
    /** Width of the soft edge. */
    feather: number;
}

const round = (v: number) => Math.round(v * 10000) / 10000;
// Typed as configs declare them; the typeof checks still reject what hand-edited JSON may hold.
const finiteOr = (value: number | undefined, fallback: number, min: number, max: number): number =>
    typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;

/** Depth and feather a new outline starts with: proportional to its size, never thinner than its own pick noise. */
export function outlineSlabDefaults(fit: Pick<SurfacePolygon, 'extent' | 'maxDeviation'>): { depth: number; feather: number } {
    const extent = Math.max(fit.extent, 1e-3);
    const depth = Math.max(fit.maxDeviation * 1.25, extent * 0.05, 0.01);
    return { depth: round(depth), feather: round(extent * 0.015) };
}

/** Every number the shader would print is finite and of a sane magnitude. */
export function outlineFitInRange(fit: SurfacePolygon): boolean {
    const ok = (v: number) => Number.isFinite(v) && Math.abs(v) <= MAX_OUTLINE_COORDINATE;
    const okPoint = (p: SurfacePoint) => ok(p.x) && ok(p.y) && ok(p.z);
    return (
        okPoint(fit.origin) && okPoint(fit.normal) && okPoint(fit.u) && okPoint(fit.v) &&
        ok(fit.extent) && ok(fit.area) && fit.outline.every((p) => ok(p.u) && ok(p.v))
    );
}

/**
 * The slab for an outline (first MAX_OUTLINE_POINTS points), with depth / feather defaulting
 * from its size. Null when it can't be drawn: fewer than three points, all on a line, or out of range.
 */
type PointList = ReadonlyArray<SurfacePoint | null | undefined> | null | undefined;

export function resolveOutlineSlab(points: PointList, depth: number | undefined, feather: number | undefined): OutlineSlab | null {
    const fit = fitSurfaceOutline(Array.isArray(points) ? points.slice(0, MAX_OUTLINE_POINTS) : []);
    if (!fit || !outlineFitInRange(fit)) return null;
    const defaults = outlineSlabDefaults(fit);
    return { fit, depth: finiteOr(depth, defaults.depth, 0.001, 1e4), feather: finiteOr(feather, defaults.feather, 0, 1e4) };
}

/** The outline's points as the slab uses them (cleaned, capped), for drawing its editor bounds. */
export function outlinePoints(points: PointList): SurfacePoint[] {
    return cleanSurfacePoints(Array.isArray(points) ? points.slice(0, MAX_OUTLINE_POINTS) : []);
}

const smoothstep = (e0: number, e1: number, x: number) => {
    const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
    return t * t * (3 - 2 * t);
};

/** CPU twin of the shader's slab weight (tests, and "which shape is under this point"). Point in runtime scene coordinates. */
export function outlineSlabWeight(slab: Pick<OutlineSlab, 'fit' | 'depth' | 'feather'>, point: SurfacePoint): number {
    const { fit, depth, feather } = slab;
    const d = { x: point.x - fit.origin.x, y: point.y - fit.origin.y, z: point.z - fit.origin.z };
    const dotp = (a: SurfacePoint) => d.x * a.x + d.y * a.y + d.z * a.z;
    const h = dotp(fit.normal);
    if (Math.abs(h) > depth) return 0;
    const pu = dotp(fit.u);
    const pv = dotp(fit.v);
    let inside = false;
    let d2 = Infinity;
    const outline = fit.outline;
    for (let i = 0; i < outline.length; i++) {
        const a = outline[i];
        const b = outline[(i + 1) % outline.length];
        const eu = b.u - a.u, ev = b.v - a.v;
        const len2 = eu * eu + ev * ev;
        if (len2 <= 1e-18) continue;
        if (a.v > pv !== b.v > pv && pu < ((b.u - a.u) * (pv - a.v)) / (b.v - a.v) + a.u) inside = !inside;
        const wu = pu - a.u, wv = pv - a.v;
        const t = Math.min(1, Math.max(0, (wu * eu + wv * ev) / len2));
        const qu = wu - eu * t, qv = wv - ev * t;
        d2 = Math.min(d2, qu * qu + qv * qv);
    }
    const half = feather / 2;
    const edge = half > 0 ? smoothstep(-half, half, (inside ? 1 : -1) * Math.sqrt(d2)) : inside ? 1 : 0;
    return edge * (1 - smoothstep(depth * 0.75, depth, Math.abs(h)));
}

/*
 * Shader layout. Every outline becomes one straight-line function with its numbers baked in as
 * literals: a slab test and a bounding-box test that reject almost every splat in a few
 * instructions, then the outline's edges (an even-odd crossing test and, for a soft edge, the
 * distance to each edge) for the splats inside the box.
 *
 * There are deliberately no constant arrays and no loops. A constant array read at a runtime
 * index is copied into per-invocation memory by SPIR-V based drivers (ANGLE on Vulkan, which
 * Chrome uses on Android and Linux, and SwiftShader), a cost paid by every splat on every frame;
 * a loop over a table that a driver unrolls per shape costs compile time instead. Straight-line
 * code grows with the outline points (capped per feature) and costs nothing for splats outside
 * the outline.
 */

/** One outline edge as the shader tests it (all numbers in the outline's 2D plane space). */
export interface OutlineShaderEdge {
    /**
     * Even-odd crossing (absent for horizontal and zero-length edges): the ray from p toward
     * +u crosses this edge when lo <= p.v < hi and (p.u - au) * dvAbs < (p.v - av) * duSigned.
     * The division-free form keeps every literal bounded by the outline's own coordinates.
     */
    cross?: { lo: number; hi: number; au: number; av: number; dvAbs: number; duSigned: number };
    /** Nearest point on the segment a + e·t, t in [0, 1] (absent for zero-length edges). */
    dist?: { au: number; av: number; eu: number; ev: number; len2: number };
}

/** The edges `outlineSlabWeight` walks, with the per-edge constants the shader prints. */
export function outlineShaderEdges(slab: Pick<OutlineSlab, 'fit'>): OutlineShaderEdge[] {
    const outline = slab.fit.outline;
    const edges: OutlineShaderEdge[] = [];
    for (let i = 0; i < outline.length; i++) {
        const a = outline[i];
        const b = outline[(i + 1) % outline.length];
        const eu = b.u - a.u;
        const ev = b.v - a.v;
        const len2 = eu * eu + ev * ev;
        // Same guard as the CPU twin: a zero-length edge neither crosses nor sets the distance.
        if (len2 <= 1e-18) continue;
        edges.push({
            ...(ev !== 0
                ? { cross: { lo: Math.min(a.v, b.v), hi: Math.max(a.v, b.v), au: a.u, av: a.v, dvAbs: Math.abs(ev), duSigned: eu * Math.sign(ev) } }
                : {}),
            dist: { au: a.u, av: a.v, eu, ev, len2 },
        });
    }
    return edges;
}

/** A literal that is safe on either side of a binary operator ("x - -1.5" never reads as "--"). */
const lit = (v: number): string => (v < 0 ? `(${shaderNumber(v)})` : shaderNumber(v));

/**
 * One outline's weight function `name(c)`: 1 inside the slab, 0 outside, soft across `feather`
 * and the slab faces. `c` is the splat centre in runtime scene coordinates. Straight-line code;
 * see the layout note above.
 */
export function outlineSlabShader(slab: Pick<OutlineSlab, 'fit' | 'depth' | 'feather'>, name: string, lang: 'glsl' | 'wgsl'): string {
    const { fit, depth, feather } = slab;
    const glsl = lang === 'glsl';
    const vec2 = glsl ? 'vec2' : 'vec2f';
    const vec3 = glsl ? 'vec3' : 'vec3f';
    const n = shaderNumber;
    const v3 = (p: SurfacePoint) => `${vec3}(${n(p.x)}, ${n(p.y)}, ${n(p.z)})`;
    let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
    for (const p of fit.outline) {
        minU = Math.min(minU, p.u);
        maxU = Math.max(maxU, p.u);
        minV = Math.min(minV, p.v);
        maxV = Math.max(maxV, p.v);
    }
    const half = feather / 2;
    const soft = half > 0;
    const edges = outlineShaderEdges(slab);
    const body: string[] = [];
    const statement = (cond: string, then: string) => (glsl ? `if (${cond}) ${then};` : `if (${cond}) { ${then}; }`);
    if (glsl) body.push('bool inside = false;');
    else body.push('var inside = false;');
    if (soft) body.push(glsl ? 'float d2 = 1e30; vec2 w; float t;' : 'var d2: f32 = 1e30; var w: vec2f; var t: f32;');
    for (const e of edges) {
        if (e.cross) {
            const c = e.cross;
            body.push(statement(`p.y >= ${lit(c.lo)} && p.y < ${lit(c.hi)} && (p.x - ${lit(c.au)}) * ${lit(c.dvAbs)} < (p.y - ${lit(c.av)}) * ${lit(c.duSigned)}`, 'inside = !inside'));
        }
        if (soft && e.dist) {
            const d = e.dist;
            const ev = `${vec2}(${n(d.eu)}, ${n(d.ev)})`;
            body.push(
                `w = p - ${vec2}(${n(d.au)}, ${n(d.av)}); t = clamp(dot(w, ${ev}) / ${lit(d.len2)}, 0.0, 1.0); w = w - ${ev} * t; d2 = min(d2, dot(w, w));`,
            );
        }
    }
    const face = `(1.0 - smoothstep(${n(depth * 0.75)}, ${n(depth)}, abs(h)))`;
    const edgeWeight = soft
        ? `smoothstep(${n(-half)}, ${n(half)}, ${glsl ? '(inside ? 1.0 : -1.0)' : 'select(-1.0, 1.0, inside)'} * sqrt(d2))`
        : glsl
          ? '(inside ? 1.0 : 0.0)'
          : 'select(0.0, 1.0, inside)';
    const bounds = `p.x < ${lit(minU - half)} || p.y < ${lit(minV - half)} || p.x > ${lit(maxU + half)} || p.y > ${lit(maxV + half)}`;
    const indent = (lines: string[]) => lines.map((l) => `    ${l}`).join('\n');
    if (glsl) {
        return `float ${name}(vec3 c) {
    vec3 d = c - ${v3(fit.origin)};
    float h = dot(d, ${v3(fit.normal)});
    if (abs(h) > ${n(depth)}) return 0.0;
    vec2 p = vec2(dot(d, ${v3(fit.u)}), dot(d, ${v3(fit.v)}));
    if (${bounds}) return 0.0;
${indent(body)}
    return ${edgeWeight} * ${face};
}`;
    }
    return `fn ${name}(c: vec3f) -> f32 {
    let d = c - ${v3(fit.origin)};
    let h = dot(d, ${v3(fit.normal)});
    if (abs(h) > ${n(depth)}) { return 0.0; }
    let p = vec2f(dot(d, ${v3(fit.u)}), dot(d, ${v3(fit.v)}));
    if (${bounds}) { return 0.0; }
${indent(body)}
    return ${edgeWeight} * ${face};
}`;
}
